import React, { Fragment, useState } from 'react';
import isNumeric from 'validator/lib/isNumeric';

import { setBirthdaySet } from '../../../../domains/Auth/slice';
import { Button } from '../../../../GlobalComponents/Button';
import { Container } from '../../../../GlobalComponents/Container';
import { ErrorMessage } from '../../../../GlobalComponents/ErrorMessage';
import { Flex } from '../../../../GlobalComponents/Flex';
import { ComboContainer } from '../../../../GlobalComponents/Form';
import { Input } from '../../../../GlobalComponents/Input';
import { Modal } from '../../../../GlobalComponents/Modal';
import { SuccessMessage } from '../../../../GlobalComponents/SuccessMessage';
import { Title } from '../../../../GlobalComponents/Title';
import { useAppDispatch, useAppSelector } from '../../../../reduxstore/hooks';
import { useAddCardMutation } from '../../../api';
import { useLogout } from '../../../hooks/useLogout';
import { setProfile } from '../../../state/profileSlice';
import ConfirmationModal from '../components/ConfirmationModal';

interface IGiftCard {
	cardNumber: string;
	error: string | null;
}

const initialValue: IGiftCard = {
	cardNumber: '',
	error: null
};

const AddCard = (): JSX.Element => {
	const [newCard, setNewCard] = useState<IGiftCard>(initialValue);
	const [hasChanged, setChanged] = useState<boolean>(false);
	const [showConfirmationDialog, setConfirmationDialog] =
		useState<boolean>(false);

	const [addNewGiftCard, { isLoading, isSuccess, isError, error }] =
		useAddCardMutation();
	const dispatch = useAppDispatch();
	const logout = useLogout();
	const userId = useAppSelector(state => state.authReducer.id);

	const toggleConfirmationDialog = () => {
		setConfirmationDialog(!showConfirmationDialog);
	};

	const terminateSession = () => {
		toggleConfirmationDialog();
		logout();
	};

	const addGiftCard = (cardNumber: number): void => {
		addNewGiftCard({
			id: userId,
			card_number: cardNumber
		})
			.unwrap()
			.then((response: ProfileResponse) => {
				dispatch(setProfile(response.data.profile));
				dispatch(setBirthdaySet(response.data.birthday_set));
				setNewCard(initialValue);
			})
			.catch(error => {
				if (error?.status === 418) {
					toggleConfirmationDialog();
				}
			});
	};

	const onChangeCard = (event: React.ChangeEvent<HTMLInputElement>): void => {
		event.persist();
		setNewCard({
			cardNumber: event.target.value,
			error: null
		});
		setChanged(true);
	};

	const onValidateCard = (event: React.FormEvent): void => {
		event.preventDefault();
		if (
			newCard.cardNumber.length === 14 &&
			isNumeric(newCard.cardNumber, { no_symbols: true })
		) {
			setChanged(false);
			setNewCard({
				...newCard,
				error: null
			});
			addGiftCard(Number(newCard.cardNumber));
		} else {
			setNewCard({
				...newCard,
				error: 'Invalid BIGGBY Card Number'
			});
		}
	};

	const AlertContent = () => (
		<Fragment>
			Due to suspicious behavior, we have disabled this account. Please
			contact
			<a href="mailto:tickets@biggby.com" style={{ padding: '0 5px' }}>
				tickets@biggby.com
			</a>
			if you need assistance with your account.
		</Fragment>
	);

	return (
		<Container>
			<Title>Add Another Card</Title>
			<Flex $flexDirection="column" $rowGap="24px">
				<ComboContainer>
					<Input
						name="anothercard"
						label=""
						type="number"
						inputmode="number"
						value={newCard.cardNumber}
						size="twoThirds"
						onChange={onChangeCard}
						$error={newCard.error}
						pattern="[0-9]*"
					/>
					<Flex
						$flexDirection="row"
						$placeContent="center flex-start"
						$alignItems="flex-start"
						$width="40%"
					>
						<Button
							onClick={onValidateCard}
							type="button"
							disabled={isLoading}
							$isLoading={isLoading}
							$buttonSize={'small'}
						>
							ADD CARD
						</Button>
					</Flex>
				</ComboContainer>

				{isSuccess && !hasChanged && (
					<SuccessMessage
						message={`BIGGBY Card Successfully Added!`}
					/>
				)}

				{isError && !hasChanged && <ErrorMessage error={error} />}

				<Modal
					isOpen={showConfirmationDialog}
					toggleHandler={toggleConfirmationDialog}
					size="sm"
					keyboard={false}
					backdrop={'static'}
				>
					<ConfirmationModal
						title="Alert"
						message={<AlertContent />}
						confirmHandler={terminateSession}
						confirmButtonText="Close"
						showCancelButton={false}
					/>
				</Modal>
			</Flex>
		</Container>
	);
};

export default AddCard;
